import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Duvet — per Quoting_markdown/duvets.md: Fabrication and Yardage are fixed
// by bed size × Self Welt (see DUVET_TABLE, sopCalculations.ts). Down
// Alternative Insert is an optional opt-in line, priced by bed size
// (DUVET_INSERT_TABLE) — not always needed, so it's a plain toggle.
//
// Unlike most item types, this form's values are submitted through dedicated
// addDuvetDraftQuoteObject/updateDuvetDraftQuoteObject mutations (see
// DraftQuoteDetail.tsx's DEDICATED_TYPE_CONFIG/handleAddObject) rather than
// the generic addDraftQuoteObject/updateDraftQuoteObject every
// non-dedicated-resolver type shares.
const DuvetForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;
  const [bedSize, setBedSize] = useState<'twin' | 'full' | 'queen' | 'king' | ''>((iv?.bedSize as any) || '');
  const [selfWelt, setSelfWelt] = useState<boolean>(iv?.weltType === 'self');
  const [duvetInsert, setDuvetInsert] = useState<boolean>(!!iv?.duvetInsert);
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!bedSize) {
        setError('Bed size is required');
        return null;
      }
      setError(null);
      return {
        bedSize,
        weltType: selfWelt ? 'self' : 'none',
        duvetInsert,
        fabricDescription: fabricDescription.trim() || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Bed Size</label>
          <select
            className="input"
            value={bedSize}
            onChange={(e) => setBedSize(e.target.value as 'twin' | 'full' | 'queen' | 'king' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="twin">Twin</option>
            <option value="full">Full</option>
            <option value="queen">Queen</option>
            <option value="king">King</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Self Welt</label>
          <select
            className="input"
            value={selfWelt ? 'yes' : 'no'}
            onChange={(e) => setSelfWelt(e.target.value === 'yes')}
            disabled={props.disabled}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Down Alternative Insert <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <select
            className="input"
            value={duvetInsert ? 'yes' : 'no'}
            onChange={(e) => setDuvetInsert(e.target.value === 'yes')}
            disabled={props.disabled}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default DuvetForm;
